// Simple AES-GCM JSON encryption using Web Crypto. Key derived via PBKDF2 from passphrase.

function getTextEncoder(): TextEncoder {
  return new TextEncoder();
}

function getTextDecoder(): TextDecoder {
  return new TextDecoder();
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function fromBase64(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const enc = getTextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    enc.encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: 100_000,
      hash: 'SHA-256'
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

export type EncryptedPayload = {
  v: 1;
  salt: string; // base64
  iv: string;   // base64
  data: string; // base64 ciphertext
};

export async function encryptJSON(passphrase: string, value: unknown): Promise<EncryptedPayload> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const plaintext = getTextEncoder().encode(JSON.stringify(value));
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { v: 1, salt: toBase64(salt), iv: toBase64(iv), data: toBase64(ciphertext) };
}

export async function decryptJSON<T = unknown>(passphrase: string, payload: EncryptedPayload): Promise<T> {
  if (payload.v !== 1) throw new Error('Unsupported payload version');
  const salt = new Uint8Array(fromBase64(payload.salt));
  const iv = new Uint8Array(fromBase64(payload.iv));
  const key = await deriveKey(passphrase, salt);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, fromBase64(payload.data));
  const text = getTextDecoder().decode(decrypted);
  return JSON.parse(text) as T;
}



















